'use client';
import React, { useRef, useState, useEffect } from 'react';
import Link from 'next/link';
import { splitHeading } from '@/constants/hooks';
import { FontAwesomeIcon, faChevronLeft, faChevronRight, faTag } from '@/constants/icons';

interface Category {
    id: number;
    name: string;
    slug: string;
}

interface Props {
    categories: Category[];
    merchantName: string;
    slug: string;
}

const MerchantCategorySlider: React.FC<Props> = ({ categories, merchantName, slug }) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [showSliders, setShowSliders] = useState(false);

    const heading = `Product Categories for ${merchantName}`;
    const [firstWord, restWords] = splitHeading(heading);

    useEffect(() => {
        const checkScroll = () => {
            if (scrollContainerRef.current) {
                const { scrollWidth, clientWidth } = scrollContainerRef.current;
                setShowSliders(scrollWidth > clientWidth);
            }
        };

        checkScroll();
        const timeout = setTimeout(checkScroll, 500);
        window.addEventListener('resize', checkScroll);
        return () => {
            clearTimeout(timeout);
            window.removeEventListener('resize', checkScroll);
        }
    }, [categories]);

    const scroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const scrollAmount = container.clientWidth * 0.6;
            container.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    if (!categories || categories.length === 0) return null;

    return (
        <section className="w-full py-12 md:py-16 bg-[#F8FAFC]">
            <style jsx>{`
                .hide-scroll::-webkit-scrollbar {
                    display: none;
                }
                .hide-scroll {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>

            <div className="container mx-auto px-4 max-w-7xl">
                {/* Section Header */}
                <div className="flex items-center gap-4 pb-6 mb-8 border-b border-[#E5E7EB]">
                    <div>
                        <h2 className="text-2xl md:text-3xl font-bold text-[#1E3A5F]">
                            {firstWord && <span className="text-[#F97316]">{firstWord} </span>}
                            <span>{restWords || heading}</span>
                        </h2>
                        <span className="text-sm text-[#F97316] font-semibold">
                            Browse by Category
                        </span>
                    </div>
                </div>

                <div className="relative group/main">
                    {/* Navigation Buttons */}
                    {showSliders && (
                        <>
                            <button
                                onClick={() => scroll('left')}
                                className="absolute -left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg border-2 border-[#E5E7EB] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white hover:border-[#1E3A5F] transition-all duration-300 opacity-0 group-hover/main:opacity-100"
                                aria-label="Scroll left"
                            >
                                <FontAwesomeIcon icon={faChevronLeft} className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => scroll('right')}
                                className="absolute -right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg border-2 border-[#E5E7EB] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white hover:border-[#1E3A5F] transition-all duration-300 opacity-0 group-hover/main:opacity-100"
                                aria-label="Scroll right"
                            >
                                <FontAwesomeIcon icon={faChevronRight} className="w-5 h-5" />
                            </button>
                        </>
                    )}

                    {/* Scrollable Category Cards */}
                    <div
                        ref={scrollContainerRef}
                        className="flex overflow-x-auto gap-4 py-4 hide-scroll scroll-smooth"
                    >
                        {categories.map((item, i) => (
                            <Link
                                key={item.id || i}
                                href={`/products/${slug}/${item.slug}`}
                                className="group/card flex-shrink-0 w-[200px] no-underline"
                            >
                                <div className="relative h-28 flex flex-col items-center justify-center bg-white rounded-xl shadow-sm hover:shadow-lg border border-[#E5E7EB] hover:border-[#F97316] transition-all duration-300 overflow-hidden group-hover/card:-translate-y-1 px-4">
                                    {/* Category Name */}
                                    <h4 className="text-sm font-bold text-[#111827] group-hover/card:text-[#1E3A5F] transition-colors duration-300 text-center leading-tight line-clamp-2">
                                        {item.name}
                                    </h4>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default MerchantCategorySlider;
